<template>
  <div data-test-id="student-create-pass-form">
    <label
      class="v3-form-label required mt-2"
      for="stu-pass-from-input"
      id="stu-pass-from-lbl"
    >
      {{ $t("create_pass_student_view.departing_from") }}
    </label>
    <LazyLoadSelect
      input-id="stu-pass-from-input"
      data-test-id="stu-pass-fromid"
      v-model="state.form.from"
      :selected="state.form.from"
      :invalid-feedback="errors.from ? $t(errors.from) : ''"
      type="combined"
      :adult-pass-request-disabled="true"
      :force-clear-cache="true"
      :placeholder="$t('create_pass_student_view.teacher_location_placeholder')"
      @changed="
        (value) => {
          state.form.from = value
          clearSubmitResponseState()
        }
      "
    >
    </LazyLoadSelect>
    <InfoBox class="danger mt-2 mb-2" v-if="state.serverErrors.from_id">
      {{ state.serverErrors.from_id[0] }}
    </InfoBox>

    <FavoriteCarousel
      data-test-id="student-from-favorites"
      :key="'departing'"
      :selected="state.form.from"
      :role="currentUser.role.name"
      :current-user="currentUser"
      :is-editable="true"
      :action="
        (elem) => {
          state.form.from = elem
        }
      "
      help-center-key="to_add_favorite_teacher_txt_v001"
      :isvalid="errors.from"
      class="mb-3"
      section="departing"
    />
    <label
      class="v3-form-label required mt-2"
      for="stu-pass-to-input"
      id="stu-pass-to-lbl"
    >
      {{ $t("create_pass_student_view.destination") }}
    </label>
    <LazyLoadSelect
      input-id="stu-pass-to-input"
      data-test-id="stu-pass-toid"
      v-model="state.form.to"
      :invalid-feedback="errors.to ? $t(errors.to) : ''"
      :is-multiple="false"
      :selected="state.form.to"
      type="combined"
      :adult-pass-request-disabled="true"
      :placeholder="$t('create_pass_student_view.teacher_location_placeholder')"
      :force-clear-cache="true"
      @changed="(value) => onChangeEvent(value)"
    >
    </LazyLoadSelect>
    <InfoBox v-if="destinationUnavailability" class="danger mt-2 mb-2">
      {{
        destinationUnavailability.unavailable_type == "App\\Models\\Room"
          ? $t("create_pass_student_view.this_room_is_unavailable_until")
          : $t("create_pass_student_view.this_adult_is_unavailable_until")
      }}
      <span
        class="mx-1"
        v-html="
          $helpers.getHTMLDateTime(
            destinationUnavailability.to_date,
            'MMMM D, YYYY',
            'h:mm A'
          ) + '.'
        "
      />
      <span v-if="destinationUnavailability.comment"
        >{{ $t("common.note") }}: {{ destinationUnavailability.comment }}</span
      >
    </InfoBox>
    <InfoBox class="danger mt-2 mb-2" v-if="state.serverErrors.to_id">
      {{ state.serverErrors.to_id[0] }}
    </InfoBox>

    <FavoriteCarousel
      data-test-id="student-to-favorites"
      :key="'destination'"
      :selected="state.form.to"
      :is-editable="true"
      :role="currentUser.role.name"
      :current-user="currentUser"
      :show-unavailables="true"
      :action="
        (elem) => {
          onChangeEvent(elem)
        }
      "
      :isvalid="errors.to || !state.isCapacityReached"
      section="destination"
    />

    <InfoBox v-if="toCapacityReached" class="danger mt-2 mb-2">
      <template v-if="!state.isToLocationQueueable">
        {{
          $t(
            "create_pass_student_view.maximum_capacity_of_the_location_has_been_reached"
          )
        }}
      </template>
      <template v-else>
        {{ $t("create_pass_student_view.this_location_is_full") }}
      </template>
    </InfoBox>

    <div
      v-if="
        state.form &&
        state.form.to &&
        state.form.to.value &&
        state.form.to.value.comment_type != 'Hidden'
      "
      class="mb-3"
    >
      <label class="v3-form-label" for="stu-pass-reason-input">
        {{ $t("common.reason") }}
      </label>
      <InputField
        id="stu-pass-reason-input"
        data-test-id="stu-pass-comment"
        v-model="state.form.reason"
        :invalid-feedback="errors.reason ? $t(errors.reason) : ''"
        max-chars="250"
        :rows="5"
        class="w-full"
        type="textarea"
        :placeholder="
          state.form.to.value.comment_type == 'Optional'
            ? $t('create_pass_student_view.reason_for_pass_optional')
            : $t('create_pass_student_view.reason_for_pass_required')
        "
      />
      <InfoBox v-if="state.serverErrors.comment" class="danger mt-2"
        >{{ state.serverErrors.comment[0] }}
      </InfoBox>
    </div>

    <InfoBox
      v-if="state.serverRes"
      :key="`${state.serverRes.title}-${state.serverRes.message}`"
      data-test-id="create-pass-form-alert"
      class="mt-3"
      announce-assertive
      :class="{ danger: Object.keys(state.serverErrors).length }"
      :title="state.serverRes.title"
    >
      {{ state.serverRes.message }}
      <div class="d-flex flex-wrap gap-2 mt-2">
        <button
          type="button"
          class="v3-base-btn full-rounded"
          data-test-id="create-pass-alert-dismiss"
          @click="dismissResponseInfoBox"
        >
          {{ $t("common.close") }}
        </button>
      </div>
    </InfoBox>

    <div class="form-group form-actions mt-2 text-center">
      <LoadingButton
        data-test-id="create-pass-form-submit"
        :is-loading="state.isLoading"
        :disabled="cooldownState.isCooldownActive"
        class="mt-2"
        rounded
        solid
        @click="submit"
      >
        {{ $t("common.submit") }}
      </LoadingButton>
    </div>
  </div>
</template>

<script>
import useCooldown from "@/composables/useCooldown.js"
import FavoriteCarousel from "@/v3components/FavoriteCarousel.vue"
import InfoBox from "@/v3components/shared/Alerts/InfoBox.vue"
import LoadingButton from "@/v3components/shared/Buttons/LoadingButton.vue"
import InputField from "@/v3components/shared/Form/InputField.vue"
import LazyLoadSelect from "@/v3components/shared/Form/LazyLoadSelect.vue"
import { useVuelidate } from "@vuelidate/core"
import { helpers, required, requiredIf } from "@vuelidate/validators"
import { computed, reactive } from "vue"
import { useRouter } from "vue-router"
import { useStore } from "vuex"

export default {
  name: "CreatePassForm",
  components: {
    LazyLoadSelect,
    FavoriteCarousel,
    InputField,
    LoadingButton,
    InfoBox
  },
  setup() {
    const store = useStore()
    const router = useRouter()

    const { state: cooldownState, startCooldown } = useCooldown()

    const state = reactive({
      isLoading: false,
      isCapacityReached: false,
      isToLocationQueueable: false,
      form: {
        from: [],
        to: [],
        reason: null
      },
      serverErrors: {},
      serverRes: null
    })

    const toCapacityReached = computed(() => {
      return state.form && state.form.to && state.isCapacityReached
    })

    const destinationUnavailability = computed(() => {
      return state.form.to && state.form.to.value
        ? state.form.to.value.unavailability
        : null
    })

    const validationMessages = {
      required: "common.this_field_is_required",
      requiredReason: "common.reason_for_pass_is_required",
      notSameAsFrom:
        "common.start_point_and_destination_point_should_not_be_the_same"
    }

    const validations = computed(() => ({
      form: {
        from: {
          required: helpers.withMessage(validationMessages.required, required)
        },
        to: {
          available: () => {
            if (
              state.form.to &&
              state.form.to.value &&
              !state.form.to.value.unavailability
            ) {
              return true
            }
          },
          notSameAsFrom: helpers.withMessage(
            validationMessages.notSameAsFrom,
            () => {
              if (
                !(state.form.from && state.form.from.value) &&
                state.form.to &&
                state.form.to.value
              ) {
                return true
              }
              if (
                !(state.form.from && state.form.from.value) ||
                !(state.form.to && state.form.to.value)
              ) {
                return false
              }
              if (state.form.from.value.type != state.form.to.value.type) {
                return true
              }

              return state.form.from.value.id != state.form.to.value.id
            }
          ),
          required: helpers.withMessage(validationMessages.required, required)
        },
        reason: {
          required: helpers.withMessage(
            validationMessages.requiredReason,
            requiredIf(() => {
              if (state.form.to && state.form.to.value) {
                return state.form.to.value.comment_type == "Optional"
                  ? false
                  : state.form.to.value.comment_type == "Hidden"
                    ? false
                    : true
              }
            })
          )
        }
      }
    }))
    const currentUser = computed(() => store.getters["authentication/user"])

    const v$ = useVuelidate(validations.value.form, state.form)

    const isValid = computed(() => !v$.value.$invalid)

    const errors = computed(() => {
      const errorObj = {}
      v$.value.$errors.forEach((err) => {
        errorObj[err.$property] = err.$message
      })
      return errorObj
    })

    const clearSubmitResponseState = () => {
      state.serverErrors = {}
      setResponseInfoBox()
    }

    const onChangeEvent = (value) => {
      clearSubmitResponseState()
      state.form.to = value
      if (value.value.type === "App\\Models\\Room") {
        store
          .dispatch("rooms/getCapacityReached", value.value.id)
          .then((response) => {
            if (response.data) {
              const data = response.data.data
              state.isCapacityReached =
                data.limit !== "-" ? data.capacity_reached : false
              state.isToLocationQueueable = data?.is_queueable || false
            }
          })
      }

      store
        .dispatch("rooms/getUnavailability", {
          id: value.value.id,
          type: value.value.type
        })
        .then((response) => {
          if (response.data.data) {
            state.form.to.value.unavailability = response.data.data
          }
        })
    }

    const submit = () => {
      if (cooldownState.isCooldownActive) {
        return
      }
      setResponseInfoBox()
      if (isValid.value) {
        const pass = {
          from_id: state.form.from.value.id,
          from_type: state.form.from.value.type,
          to_id: state.form.to.value.id,
          to_type: state.form.to.value.type,
          comment: state.form.reason
        }
        state.isLoading = true
        store
          .dispatch("passes/createPass", pass)
          .then((response) => {
            state.serverErrors = {}
            state.isLoading = false

            if (response.data && response.data.data) {
              const data = response.data.data
              const params = {
                from_id: data.from_id,
                from_type: data.from_type,
                to_id: data.to_id,
                to_type: data.to_type
              }
              store.dispatch("passes/getActiveFlags", params)
            }
            router.push("/passes/activepass")
          })
          .catch((err) => {
            state.isLoading = false
            const response = err?.response?.data ? err.response.data : err
            state.serverErrors = response?.errors ? response.errors : {}
            setResponseInfoBox("Error", response?.message)
            startCooldown() // Start 30-second cooldown
          })
      } else {
        v$.value.$touch()
      }
    }

    const dismissResponseInfoBox = () => setResponseInfoBox()

    const setResponseInfoBox = (title, message) => {
      if (title || message) {
        state.serverRes = {
          message,
          title
        }
      } else {
        state.serverRes = null
      }
    }

    return {
      state,
      cooldownState,
      destinationUnavailability,
      toCapacityReached,
      onChangeEvent,
      submit,
      dismissResponseInfoBox,
      errors,
      currentUser,
      clearSubmitResponseState
    }
  }
}
</script>

<style></style>
